import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button, HStack, Dialog, Portal, CloseButton, Table, Text, Box } from "@chakra-ui/react";
import { JulianDate } from "cesium";
import { useAppStore } from "../../../context/store";
import { Tooltip } from "../../ui/tooltip";
import { LuDraftingCompass, LuFocus } from "react-icons/lu";
import Type from "../../../utilies/Types";
import { useMapObjects } from "../../../context/MapObjectsContext";
import { getCombinedKeysText, matchKeyInText } from "../../../utilies/Utility";
import { createAutoSketcher } from "../../../utilies/autoSketcher/AutoSketcher";
import { blinkEntity } from "../../../utilies/EntityUtility";

const getCesiumPropValue = (entity, key) => {
  const p = entity?.properties?.[key];
  if (!p) return null;
  try {
    return typeof p.getValue === "function" ? p.getValue(JulianDate.now()) : p;
  } catch {
    return null;
  }
};

const getOwnerLayerName = (entity) => {
  const owner = entity?.entityCollection?.owner;
  return owner?.layerName ?? owner?.name ?? "";
};

const RejectRow = React.memo(function RejectRow({ row, onFocus, onForce }) {
  return (
    <Table.Row>
      <Table.Cell>{row.propertiesId ?? "-"}</Table.Cell>
      <Table.Cell>
        <Button variant="ghost" size="sm" onClick={() => onFocus(row.entity)}>
          <LuFocus />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onForce(row.entity, row.cesiumEntityId)}>
          <LuDraftingCompass />
        </Button>
      </Table.Cell>
    </Table.Row>
  );
});

function AutoSketcher() {
  const viewerRef = useAppStore((s) => s.viewerRef);
  const editMode = useAppStore((s) => s.editMode);
  const autoSketcherMode = useAppStore((s) => s.autoSketcherMode);
  const userType = useAppStore((s) => s.userType);
  const autoSketcherDistance = useAppStore((s) => s.autoSketcherDistance);
  const savedHotkeysRef = useAppStore((s) => s.savedHotkeysRef);
  const userData = useAppStore((s) => s.userData);

  const { mapObjects, setMapObjects } = useMapObjects();
  const setAutoSketcherMode = useAppStore.getState().setAutoSketcherMode;

  const toolDestroyRef = useRef(null);
  const toolApiRef = useRef(null);

  const dsListenerRef = useRef(null);
  const pendingRejectRef = useRef(false);

  const [show, setShow] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  // Store rows (plain data) instead of recomputing on every render.
  const [rejectedRows, setRejectedRows] = useState([]); // [{ entity, cesiumEntityId, propertiesId, layerName }]

  useEffect(() => {
    const isAdmin = userType?.includes(Type.ROLE.ADMIN) || userType?.includes(Type.ROLE.SUPER_ADMIN);
    if (!isAdmin) {
      setShow(false);
      return;
    }
    const ok = Array.isArray(mapObjects) && mapObjects.some(({ file_name }) => !!file_name && matchKeyInText(file_name, "virtualline"));
    setShow(ok);
  }, [mapObjects, userType]);

  // Hotkeys
  useEffect(() => {
    const isMod = (k) => ["shift", "ctrl", "alt", "meta"].includes(String(k).toLowerCase());
    const isAlpha = (k) => /^[a-z]$/i.test(String(k || ""));
    const normalizeKey = (k) => {
      let key = String(k || "").toLowerCase();
      if (key === "control") key = "ctrl";
      if (key === " ") key = "space";
      return key;
    };

    const getCombo = (type) => savedHotkeysRef?.current?.find?.((h) => h.type === type)?.combination || [];

    const matches = (e, combo) => {
      if (!combo || combo.length === 0) return false;

      const norm = combo.map(normalizeKey);

      const want = {
        shift: norm.includes("shift"),
        ctrl: norm.includes("ctrl"),
        alt: norm.includes("alt"),
        meta: norm.includes("meta"),
      };

      if (e.shiftKey !== want.shift) return false;
      if (e.ctrlKey !== want.ctrl) return false;
      if (e.altKey !== want.alt) return false;
      if (e.metaKey !== want.meta) return false;

      const main = norm.find((k) => !isMod(k));
      if (!main) return false;

      return normalizeKey(e.key) === normalizeKey(main);
    };

    const shouldIgnoreKeydown = (e) => {
      const el = e?.target;
      const tag = el?.tagName?.toLowerCase();
      return tag === "input" || tag === "textarea" || el?.isContentEditable;
    };

    // -------------------------
    // 3-key sequential support
    // combo: [mod, letter1, letter2]
    // user: mod+letter1, then within 600ms press letter2 (with same mods or no mods)
    // -------------------------
    const PENDING_KEY = "__autosketcher_hotkey_pending__";
    if (!window[PENDING_KEY]) {
      window[PENDING_KEY] = {
        active: false,
        type: null,
        mods: [],
        stepKey: "",
        finalKey: "",
        expiresAt: 0,
        tid: null,
      };
    }
    const pending = window[PENDING_KEY];

    const resetPending = () => {
      if (pending.tid) clearTimeout(pending.tid);
      pending.active = false;
      pending.type = null;
      pending.mods = [];
      pending.stepKey = "";
      pending.finalKey = "";
      pending.expiresAt = 0;
      pending.tid = null;
    };

    const modsMatchExactly = (e, mods) => {
      const needShift = mods.includes("shift");
      const needCtrl = mods.includes("ctrl");
      const needAlt = mods.includes("alt");
      const needMeta = mods.includes("meta");

      if (e.shiftKey !== needShift) return false;
      if (e.ctrlKey !== needCtrl) return false;
      if (e.altKey !== needAlt) return false;
      if (e.metaKey !== needMeta) return false;
      return true;
    };

    const modsAreNone = (e) => !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey;

    const isSequential3Combo = (combo) => {
      if (!combo || combo.length !== 3) return false;

      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));

      return mods.length === 1 && nonMods.length === 2 && isAlpha(nonMods[0]) && isAlpha(nonMods[1]);
    };

    const getSequentialParts = (combo) => {
      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));
      return { mods, stepKey: nonMods[0], finalKey: nonMods[1] };
    };

    const startPending = (type, mods, stepKey, finalKey) => {
      resetPending();
      pending.active = true;
      pending.type = type;
      pending.mods = mods;
      pending.stepKey = stepKey;
      pending.finalKey = finalKey;
      pending.expiresAt = Date.now() + 3000;
      pending.tid = setTimeout(() => resetPending(), 3000);
    };

    const runAction = (type, e) => {
      if (type === Type.HOTKEYS.AUTOSKETCHER) {
        e.preventDefault();
        setAutoSketcherMode((v) => !v);
        return true;
      }
      return false;
    };

    const handleKeyDown = (e) => {
      if (shouldIgnoreKeydown(e)) return;
      if (editMode) return;
      if (!show) return;

      const key = normalizeKey(e.key);

      if (key === "escape") {
        if (pending.active) resetPending();

        if (autoSketcherMode) {
          e.preventDefault();
          setAutoSketcherMode(false);
        }
        if (rejectOpen) {
          e.preventDefault();
          setRejectOpen(false);
        }
        return;
      }

      // 1) pending final key check
      if (pending.active) {
        if (Date.now() > pending.expiresAt) {
          resetPending();
        } else {
          const okMods = modsMatchExactly(e, pending.mods) || modsAreNone(e);

          if (okMods && key === normalizeKey(pending.finalKey)) {
            e.preventDefault();
            const type = pending.type;
            resetPending();
            runAction(type, e);
            return;
          }

          if (!isMod(key)) resetPending();
        }
      }

      // 2) handle autosketcher hotkey
      const type = Type.HOTKEYS.AUTOSKETCHER;
      const combo = getCombo(type);

      // sequential start
      if (isSequential3Combo(combo)) {
        const { mods, stepKey, finalKey } = getSequentialParts(combo);

        if (modsMatchExactly(e, mods) && key === normalizeKey(stepKey)) {
          e.preventDefault();
          startPending(type, mods, stepKey, finalKey);
          return;
        }
      }

      // normal one-shot
      if (matches(e, combo)) {
        resetPending();
        runAction(type, e);
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      resetPending();
    };
  }, [editMode, show, autoSketcherMode, rejectOpen, savedHotkeysRef, setAutoSketcherMode]);

  // Tool lifecycle
  useEffect(() => {
    const cleanup = ({ keepApi = false } = {}) => {
      try {
        toolDestroyRef.current?.destroy?.();
      } catch {}
      toolDestroyRef.current = null;

      if (!keepApi) toolApiRef.current = null;

      try {
        const ds = dsListenerRef.current?.ds;
        const fn = dsListenerRef.current?.fn;
        if (ds?.entities?.collectionChanged && fn) ds.entities.collectionChanged.removeEventListener(fn);
      } catch {}
      dsListenerRef.current = null;
    };

    const keepApi = rejectOpen || pendingRejectRef.current;

    if (!show) {
      cleanup({ keepApi: false });
      pendingRejectRef.current = false;
      if (autoSketcherMode) setAutoSketcherMode(false);
      return;
    }

    if (!autoSketcherMode) {
      cleanup({ keepApi });
      return;
    }

    const viewer = viewerRef?.current;
    if (!viewer) {
      setAutoSketcherMode(false);
      cleanup({ keepApi: false });
      return;
    }

    const virtualObj = Array.isArray(mapObjects) ? mapObjects.find((o) => matchKeyInText(o?.file_name, "virtualline")) : null;
    const targetDataSource = virtualObj?.dataSource;

    if (!targetDataSource?.entities) {
      setAutoSketcherMode(false);
      cleanup({ keepApi: false });
      return;
    }

    cleanup({ keepApi: false });

    const onCollectionChanged = (_collection, added) => {
      if (!added || added.length === 0) return;

      setMapObjects((prev) => {
        if (!Array.isArray(prev)) return prev;

        let changed = false;
        const next = prev.map((o) => {
          if (o?.dataSource === targetDataSource && o?.is_edited !== true) {
            changed = true;
            return { ...o, is_edited: true };
          }
          return o;
        });

        return changed ? next : prev;
      });
    };

    targetDataSource.entities.collectionChanged.addEventListener(onCollectionChanged);
    dsListenerRef.current = { ds: targetDataSource, fn: onCollectionChanged };

    const offsetMetersRaw = parseFloat(autoSketcherDistance);
    const offsetMeters = Number.isFinite(offsetMetersRaw) ? offsetMetersRaw : 0.2;

    const tool = createAutoSketcher(viewer, targetDataSource, {
      userData: userData,
      offsetMeters,
      maxCurvatureDeg: 8,
      layerNameAllowList: ["curbstone", "road edge", "concrete barrier", "guardrail"],
      onCurvatureReject: (ents) => {
        pendingRejectRef.current = true;
        const list = Array.isArray(ents) ? ents : [];

        // Build rows once (fast UI on large lists).
        const rows = list.map((e, idx) => ({
          idx: idx + 1,
          entity: e,
          cesiumEntityId: e?.id ?? "",
          propertiesId: getCesiumPropValue(e, "id"),
          layerName: getOwnerLayerName(e),
        }));

        setRejectedRows(rows);
        if (rows.length > 0) setRejectOpen(true);
      },
      onDone: () => {
        setAutoSketcherMode(false);
      },
    });

    toolDestroyRef.current = tool;
    toolApiRef.current = tool;

    return () => cleanup({ keepApi: rejectOpen || pendingRejectRef.current });
  }, [autoSketcherMode, show, rejectOpen, mapObjects, viewerRef, setAutoSketcherMode, setMapObjects, autoSketcherDistance]);

  const toggle = () => setAutoSketcherMode((v) => !v);

  const removeRejectedRow = useCallback((cesiumEntityId) => {
    if (!cesiumEntityId) return;
    setRejectedRows((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const next = prev.filter((r) => r?.cesiumEntityId !== cesiumEntityId);

      if (next.length === 0) {
        setRejectOpen(false);
        pendingRejectRef.current = false;
      }
      return next;
    });
  }, []);

  const onFocus = useCallback(
    (entity) => {
      blinkEntity(viewerRef.current, entity, { zoom: true });
    },
    [viewerRef],
  );

  const onForce = useCallback(
    (entity, cesiumEntityId) => {
      const api = toolApiRef.current;
      if (!api?.processOneEntity) {
        console.warn("[AutoSketcher] tool api is not available");
        return;
      }
      const res = api.processOneEntity(entity, { force: true });
      if (res?.ok) removeRejectedRow(cesiumEntityId);
    },
    [removeRejectedRow],
  );

  if (!show) return null;

  return (
    <>
      <HStack>
        <Button disabled={editMode} variant="solid" p={0} opacity={1} bg={autoSketcherMode ? "green" : "white"} color={autoSketcherMode ? "white" : "MenuText"} onClick={toggle}>
          <Tooltip content={"Auto Sketcher " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.AUTOSKETCHER)}>
            <LuDraftingCompass />
          </Tooltip>
        </Button>
      </HStack>

      <Dialog.Root
        modal={false}
        open={rejectOpen}
        onOpenChange={(e) => {
          const open = !!e.open;
          setRejectOpen(open);

          if (!open) {
            pendingRejectRef.current = false;
            setRejectedRows([]);
            if (!autoSketcherMode) toolApiRef.current = null;
          }
        }}
        size="lg"
        lazyMount
        unmountOnExit
        closeOnInteractOutside={false}
      >
        <Portal>
          <Dialog.Positioner style={{ pointerEvents: "none" }}>
            <Dialog.Content style={{ pointerEvents: "auto" }} maxH="50vh" overflow="hidden">
              <Dialog.Header>
                <Dialog.Title>Entities rejected (curvature &gt; 8°)</Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <CloseButton />
                </Dialog.CloseTrigger>
              </Dialog.Header>

              <Dialog.Body overflow="auto">
                {rejectedRows.length === 0 ? (
                  <Text>No rejected entities.</Text>
                ) : (
                  <Box overflowX="auto">
                    <Table.Root size="sm" variant="outline">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>ID</Table.ColumnHeader>
                          <Table.ColumnHeader>Actions</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>

                      <Table.Body>
                        {rejectedRows.map((r) => (
                          <RejectRow key={`${r.cesiumEntityId}`} row={r} onFocus={onFocus} onForce={onForce} />
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                )}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
}

export default AutoSketcher;
